import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiExternalLink, FiPlus, FiStar, FiTrash2 } from "react-icons/fi";
import firebase from "firebase/app";
import "firebase/firestore";

import { db } from "../../../firebase/firebase.utils";

const PASTELS = [
  "#f3d9e7",
  "#dde7ff",
  "#d8f3e6",
  "#ffe7d6",
  "#e8d9ff",
  "#dff2ff",
  "#ffe1ea",
  "#e6f0d6",
];

const createdMillis = (post) => post?.createdAt?.toMillis?.() || 0;

const hash = (value = "") => {
  let result = 0;
  for (let index = 0; index < value.length; index += 1) {
    result = (result * 31 + value.charCodeAt(index)) >>> 0;
  }
  return result;
};

const coverFor = (post) => {
  if (post?.coverMediaUrl) return post.coverMediaUrl;
  const first = Array.isArray(post?.media) ? post.media[0] : null;
  if (!first) return null;
  if (first.coverUrl) return first.coverUrl;
  return (first.kind || first.type) === "image"
    ? first.url || first.mediaUrl
    : null;
};

export default function AccountPostsManager({ uid }) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [featuredPostIds, setFeaturedPostIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!uid) return undefined;

    const unsubscribePosts = db
      .collection("posts")
      .where("authorId", "==", uid)
      .limit(40)
      .onSnapshot(
        (snapshot) => {
          setPosts(
            snapshot.docs
              .map((document) => ({
                id: document.id,
                ...document.data(),
              }))
              .filter((post) => !post.isDeleted)
              .sort((a, b) => createdMillis(b) - createdMillis(a)),
          );
          setLoading(false);
        },
        (error) => {
          console.log("Account posts load error:", error);
          setLoading(false);
        },
      );

    const unsubscribeUser = db
      .collection("users")
      .doc(uid)
      .onSnapshot(
        (snapshot) => {
          const ids = snapshot.data()?.featuredPostIds;
          setFeaturedPostIds(Array.isArray(ids) ? ids.filter(Boolean) : []);
        },
        (error) => console.log("Featured posts listener error:", error),
      );

    return () => {
      unsubscribePosts();
      unsubscribeUser();
    };
  }, [uid]);

  const featuredSet = useMemo(
    () => new Set(featuredPostIds),
    [featuredPostIds],
  );

  const toggleFeatured = async (post) => {
    if (!uid || !post?.id || busy) return;
    const nextFeatured = !featuredSet.has(post.id);

    try {
      setBusy(`feature:${post.id}`);
      const batch = db.batch();
      batch.set(
        db.collection("users").doc(uid),
        {
          featuredPostIds: nextFeatured
            ? firebase.firestore.FieldValue.arrayUnion(post.id)
            : firebase.firestore.FieldValue.arrayRemove(post.id),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
      batch.set(
        db.collection("posts").doc(post.id),
        {
          isPinned: nextFeatured,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
      await batch.commit();
    } catch (error) {
      console.log("Featured post update error:", error);
      window.alert("The featured post selection could not be updated.");
    } finally {
      setBusy("");
    }
  };

  const removePost = async (post) => {
    if (!uid || !post?.id || busy) return;
    if (!window.confirm("Delete this post? This cannot be undone.")) return;

    try {
      setBusy(`delete:${post.id}`);
      const batch = db.batch();
      batch.delete(db.collection("posts").doc(post.id));
      batch.set(
        db.collection("users").doc(uid),
        {
          featuredPostIds:
            firebase.firestore.FieldValue.arrayRemove(post.id),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
      await batch.commit();
    } catch (error) {
      console.log("Account post delete error:", error);
      window.alert("The post could not be deleted.");
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="account-post-manager">
      <header>
        <div>
          <h2>Your posts</h2>
          <p>Feature posts on your profile or remove posts you no longer need.</p>
        </div>
        <button type="button" onClick={() => navigate("/post/new")}>
          <FiPlus /> New post
        </button>
      </header>

      {loading ? (
        <div className="account-post-manager__state">Loading posts…</div>
      ) : !posts.length ? (
        <div className="account-post-manager__state">No posts yet.</div>
      ) : (
        <div className="account-post-manager__grid">
          {posts.map((post) => {
            const coverUrl = coverFor(post);
            const featured = featuredSet.has(post.id);

            return (
              <article key={post.id}>
                <button
                  type="button"
                  className="account-post-manager__cover"
                  onClick={() => navigate(`/post/${post.id}`)}
                  style={{
                    backgroundColor: PASTELS[hash(post.id) % PASTELS.length],
                  }}
                >
                  {coverUrl ? (
                    <img src={coverUrl} alt="" loading="lazy" />
                  ) : (
                    <span>{post.text?.trim() || "Post"}</span>
                  )}
                  <FiExternalLink className="account-post-manager__open" />
                </button>

                <div className="account-post-manager__actions">
                  <button
                    type="button"
                    className={featured ? "is-featured" : ""}
                    disabled={!!busy}
                    onClick={() => toggleFeatured(post)}
                    aria-label={
                      featured ? "Remove from featured posts" : "Feature post"
                    }
                    title={featured ? "Unfeature" : "Feature"}
                  >
                    <FiStar />
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={!!busy}
                    onClick={() => removePost(post)}
                    aria-label="Delete post"
                    title="Delete"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
